import React, { useEffect, useMemo, useRef, useState } from "react";
import { previewEmail } from "../../../../../../APIs/Messages/PreviewEmail";
import { sendEmail } from "../../../../../../APIs/Messages/SendEmail";
import { useAppContext } from "../../../../../../hooks/useAppContext";
import { useBookingPermissions } from "../../../../../../hooks/useBookingPermissions";
import { useDiaryContext } from "../../../../../../hooks/useDiaryContext";
import { useNotificationsContext } from "../../../../../../hooks/useNotificationsContext";
import { useUserContext } from "../../../../../../hooks/useUserContext";
import { Button } from "../../../../../Shared/Buttons/Button";
import { EDSButton } from "../../../../../eds/EDSButton";
import DetachedModal from "../../../../../Shared/Modals/DetachedModal/DetachedModal";
import { Checkbox } from "../../../../../ui/checkbox";
import { Input } from "../../../../../ui/input";
import { Label } from "../../../../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../../../ui/select";
import { Spinner } from "../../../../../ui/spinner";
import AttachmentsList from "./AttachmentsList";
import HtmlEditor from "./HtmlEditor";
import {
  addAttachment,
  getBookingDetails,
  getEmailFrom,
  getNewEmail,
  getPredefinedEmailAttachments,
  getPredefinedEmailMessages,
  newEmailType
} from "./SendEmailController";

type SendEmailType = {
  handleClose: () => void;
  handleSent: () => void;
  initialEmailType?: string;
};

interface SendEmailDependencies {
  selectedBooking: ReturnType<typeof useDiaryContext>["selectedBooking"];
  selectedBookingV4: ReturnType<typeof useDiaryContext>["selectedBookingV4"];
  venueGroupConfig: ReturnType<typeof useAppContext>["venueGroupConfig"];
  venueConfig: ReturnType<typeof useDiaryContext>["venueConfig"];
  user: ReturnType<typeof useUserContext>["user"];
  email: newEmailType | undefined;
}

interface EmailTypeButtonsProps {
  email: newEmailType;
  updateEmail: (props: Partial<newEmailType>) => void;
  userCanManageBooking: boolean;
  userIsInternal: boolean;
  userCanAccessBooking: boolean;
  showPreOrders: boolean;
}

const EmailTypeButtons = ({
  email,
  updateEmail,
  userCanManageBooking,
  userIsInternal,
  userCanAccessBooking,
  showPreOrders
}: EmailTypeButtonsProps) => {
  const showSection =
    userCanManageBooking ||
    userIsInternal ||
    userCanAccessBooking ||
    showPreOrders;

  if (!showSection) {
    return null;
  }

  return (
    <div className="SendEmailSection flex flex-col gap-2 flex-1">
      <div className="SendEmailSection-Label font-medium text-[var(--textColor)] text-sm">
        Type of email:
      </div>
      <div className="SendEmailSection-ButtonGroup flex flex-wrap gap-2">
        {userCanManageBooking && (
          <EDSButton
            variant={email.type === "general" ? "default" : "outline"}
            size="sm"
            onClick={() => updateEmail({ type: "general" })}
            className="justify-start"
          >
            General
          </EDSButton>
        )}
        {userIsInternal && (
          <EDSButton
            variant={email.type === "availability" ? "default" : "outline"}
            size="sm"
            onClick={() => updateEmail({ type: "availability" })}
            className="justify-start"
          >
            Availability
          </EDSButton>
        )}
        {userCanAccessBooking && (
          <EDSButton
            variant={email.type === "confirmation" ? "default" : "outline"}
            size="sm"
            onClick={() => updateEmail({ type: "confirmation" })}
            className="justify-start"
          >
            Confirmation
          </EDSButton>
        )}
        {userCanManageBooking && (
          <EDSButton
            variant={email.type === "cancellation" ? "default" : "outline"}
            size="sm"
            onClick={() => updateEmail({ type: "cancellation" })}
            className="justify-start"
          >
            Cancellation
          </EDSButton>
        )}
        {showPreOrders && (
          <EDSButton
            variant={email.type === "preorder" ? "default" : "outline"}
            size="sm"
            onClick={() => updateEmail({ type: "preorder" })}
            className="justify-start"
          >
            Pre-order
          </EDSButton>
        )}
      </div>
    </div>
  );
};

interface IncludeOptionsProps {
  email: newEmailType;
  updateEmail: (props: Partial<newEmailType>) => void;
  userCanAccessBooking: boolean;
  showCustomerRequests: boolean;
}

const IncludeOptions = ({
  email,
  updateEmail,
  userCanAccessBooking,
  showCustomerRequests
}: IncludeOptionsProps) => {
  const showSection = userCanAccessBooking || showCustomerRequests;

  if (!showSection) {
    return null;
  }

  return (
    <div className="SendEmailSection flex flex-col gap-2 flex-1">
      <div className="SendEmailSection-Label font-medium text-[var(--textColor)] text-sm">
        Include:
      </div>
      <div className="SendEmailSection-Data flex flex-col gap-2">
        {userCanAccessBooking && (
          <div className="flex items-center space-x-2">
            <Checkbox
              id="includeSummary"
              checked={email.includeSummary}
              onCheckedChange={(checked) => {
                updateEmail({ includeSummary: !!checked });
              }}
            />
            <Label htmlFor="includeSummary">Booking summary</Label>
          </div>
        )}
        {showCustomerRequests && (
          <div className="flex items-center space-x-2">
            <Checkbox
              id="includeCustomerRequests"
              checked={email.includeCustomerRequests}
              onCheckedChange={(checked) => {
                updateEmail({ includeCustomerRequests: !!checked });
              }}
            />
            <Label htmlFor="includeCustomerRequests">Special Requests</Label>
          </div>
        )}
      </div>
    </div>
  );
};

const hasRequiredDependencies = ({
  selectedBooking,
  selectedBookingV4,
  venueGroupConfig,
  venueConfig,
  user,
  email
}: SendEmailDependencies): boolean => {
  return Boolean(
    selectedBooking &&
    selectedBookingV4 &&
    venueGroupConfig &&
    venueConfig &&
    user &&
    email
  );
};

const SendEmail = ({
  handleClose,
  handleSent,
  initialEmailType
}: SendEmailType) => {
  const { selectedBooking, selectedBookingV4, venueConfig } = useDiaryContext();
  const { venueGroupConfig } = useAppContext();
  const { user } = useUserContext();
  const notificationsContext = useNotificationsContext();
  const [sendFrom, setSendFrom] = useState<"site" | "me">("me");
  const [selectedAttachment, setSelectedAttachment] = useState<string>("");
  const portalContainerRef = useRef<HTMLDivElement>(null);
  const [selectedAttachments, setSelectedAttachments] = useState<File[]>();
  const [preDefinedMessage, setPreDefinedMessage] = useState<string>("");
  const [emailPreview, setEmailPreview] = useState<string>("");
  const [sending, setSending] = useState<boolean>(false);
  const [loadingPreview, setLoadingPreview] = useState<boolean>(false);

  const [email, setEmail] = useState<newEmailType | undefined>(() => {
    const newEmail = getNewEmail({
      selectedBooking,
      venueConfig,
      venueGroupConfig,
      user
    });
    if (newEmail && initialEmailType) {
      return { ...newEmail, type: initialEmailType };
    }
    return newEmail;
  });

  const updateEmail = (props: Partial<newEmailType>) => {
    if (!email) {
      return;
    }

    setEmail({ ...email, ...props });
  };

  useEffect(() => {
    // immediately reset dropdown selection to show blank
    setSelectedAttachment("");
  }, [selectedAttachment]);

  useEffect(() => {
    // handle file upload selection
    uploadAttachment();
  }, [selectedAttachments]);

  useEffect(() => {
    if (!preDefinedMessage || !email) {
      return;
    }
    const message = email.message + preDefinedMessage;
    updateEmail({ message });
    // immediately reset dropdown selection to show blank
    setPreDefinedMessage("");
  }, [preDefinedMessage]);

  const uploadAttachment = async () => {
    if (!selectedAttachments || !email) {
      return;
    }

    const response = await addAttachment({
      bookingId: selectedBooking?.id ?? "",
      files: selectedAttachments
    });

    if (response.error) {
      // upload failed
      notificationsContext.addNotification({
        heading: `Error: ${response.errorMessage}`,
        autoClose: 4000,
        toastType: "error"
      });
    }

    if (response.attachments && response.attachments.length > 0) {
      // upload success - add to new email object
      const attachments = email.attachments;
      updateEmail({
        attachments: attachments.concat(response.attachments)
      });
    }

    // reset selection - this resets the upload state to remove the clear button
    setSelectedAttachments(undefined);
  };

  // get predefined email messages for dropdown
  const preDefinedMessages = useMemo(
    () => getPredefinedEmailMessages({ venueConfig, venueGroupConfig }),
    [venueConfig, venueGroupConfig]
  );

  // get predefined email attachments for dropdown
  const preDefinedAttachments = useMemo(
    () => getPredefinedEmailAttachments({ venueConfig, venueGroupConfig }),
    [venueConfig, venueGroupConfig]
  );

  // Handle predefined attachment selection
  const handleAttachmentSelect = (attachmentId: string) => {
    setSelectedAttachment(attachmentId);
    if (!attachmentId || !email) {
      return;
    }

    const preDefinedSelectedAttachments =
      email.preDefinedSelectedAttachments ?? [];

    const attachment = preDefinedAttachments.find(
      (a) => a._id === attachmentId
    );

    const alreadyAdded = email.preDefinedSelectedAttachments?.find(
      (a) => a === attachment
    );

    if (attachment && !alreadyAdded) {
      preDefinedSelectedAttachments.push(attachment);
    }

    updateEmail({
      preDefinedSelectedAttachments
    });
  };

  // Handle file upload with size validation
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      setSelectedAttachments(undefined);
      return;
    }

    const file = files[0];
    const maxSizeInBytes = 10 * 1024 * 1024; // 10MB limit

    if (file.size > maxSizeInBytes) {
      notificationsContext.addNotification({
        heading: `File size exceeds 10MB limit. Please select a smaller file.`,
        autoClose: 4000,
        toastType: "error"
      });
      e.target.value = "";
      return;
    }

    setSelectedAttachments(Array.from(files));
  };

  // props for `type of email` options - derived from permissions
  const { userCanManageBooking, userCanConfirmBooking, userCanCreateMessages } =
    useBookingPermissions();
  const userIsInternal = false;
  const userCanAccessBooking = userCanManageBooking || userCanConfirmBooking;
  const showPreOrders = selectedBooking?.hasPreorders && userCanManageBooking;
  const showCustomerRequests =
    email?.type === "confirmation" && !!selectedBookingV4?.notes;

  // get booking details
  const bookingDetails = useMemo(
    () => getBookingDetails({ selectedBooking }),
    [selectedBooking, selectedBookingV4]
  );

  const showEmailPreview = !!emailPreview;

  const dependenciesReady = hasRequiredDependencies({
    selectedBooking,
    selectedBookingV4,
    venueGroupConfig,
    venueConfig,
    user,
    email
  });

  if (!dependenciesReady) {
    return <></>;
  }

  // Type assertions after dependency check - TypeScript doesn't narrow these automatically
  const validEmail = email as newEmailType;
  const validSelectedBooking = selectedBooking!;
  const validUser = user!;
  const validVenueConfig = venueConfig!;
  const validVenueGroupConfig = venueGroupConfig!;
  const validShowPreOrders = !!showPreOrders;

  return (
    <DetachedModal
      heading={showEmailPreview ? "Preview email" : "Compose Email"}
      width="70%"
      height="100%"
      handleClose={handleClose}
    >
      <div
        className={[
          "SendEmailModal flex flex-col h-full min-h-0 gap-4",
          showEmailPreview
            ? "SendEmailModal-Preview h-[calc(100vh-160px)] min-h-[500px]"
            : ""
        ]
          .filter(Boolean)
          .join(" ")}
        ref={portalContainerRef}
      >
        <div
          className={[
            "ScrollWrapper overflow-auto grow min-h-[400px] flex flex-col pr-1",
            showEmailPreview
              ? "ScrollWrapper-Preview overflow-hidden !min-h-0 flex-auto"
              : ""
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {(sending || loadingPreview) && (
            <div className="flex items-center justify-center h-full">
              <Spinner className="size-8" />
            </div>
          )}
          {showEmailPreview && !loadingPreview ? (
            <div className="PreviewEmailModal-Body w-full flex-1 flex flex-col min-h-0 h-full">
              <iframe
                title="Email preview"
                className="PreviewEmailModal-Iframe w-full flex-1 min-h-[400px] h-full border-none bg-[var(--defaultEmailBackground)]"
                src={URL.createObjectURL(
                  new Blob([emailPreview], {
                    type: "text/html"
                  })
                )}
              />
            </div>
          ) : (
            <>
              <div className="SendEmailModal-Header flex flex-col gap-1">
                <div className="SendEmailContent flex flex-col gap-4">
                  <div className="SendEmailSection flex flex-col gap-2 flex-1">
                    <div className="SendEmailSection-Label font-medium text-[var(--textColor)] text-sm">
                      From:
                    </div>
                    <div className="SendEmailSection-Data Multi flex flex-row items-center justify-between flex-wrap gap-3">
                      <div className="EmailName text-sm text-[var(--textColor)]">
                        {validEmail.fromName}
                        <span className="emailAddress text-[var(--tertiaryColor)] font-[var(--font-family)]">
                          {` <${validEmail.fromEmail}>`}
                        </span>
                      </div>
                      <Button
                        className="SendFromButton shrink-0"
                        type="default"
                        onClick={() => {
                          const from = sendFrom === "me" ? "site" : "me";
                          updateEmail(
                            getEmailFrom({
                              from: sendFrom,
                              user: validUser,
                              venueConfig: validVenueConfig,
                              venueGroupConfig: validVenueGroupConfig
                            })
                          );
                          setSendFrom(from);
                        }}
                      >{`Send from ${sendFrom}`}</Button>
                    </div>
                  </div>
                  <div className="SendEmailSection-Row flex flex-row flex-wrap gap-4 justify-between items-start max-md:flex-col max-md:gap-4">
                    <div className="SendEmailSection flex flex-col gap-2 flex-1">
                      <div className="SendEmailSection-Label font-medium text-[var(--textColor)] text-sm">
                        To:
                      </div>
                      <div className="SendEmailSection-Data flex flex-col gap-2">
                        {validEmail.to?.map((t) => (
                          <div
                            key={t.address}
                            className="EmailName text-sm text-[var(--textColor)]"
                          >
                            {`${t.name}`}
                            <span className="emailAddress text-[var(--tertiaryColor)] font-[var(--font-family)]">
                              {` <${t.address}> `}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="SendEmailSection SendEmailSection-Right flex flex-col gap-2 flex-1 items-end text-right max-md:items-start max-md:text-left">
                      <div className="SendEmailSection-Label font-medium text-[var(--textColor)] text-sm">
                        Cc:
                      </div>
                      <div className="SendEmailSection-Data flex flex-col gap-2">
                        <Input
                          value={validEmail.cc}
                          type="email"
                          placeholder="Enter email address"
                          autoComplete="off"
                          onChange={(e) =>
                            updateEmail({
                              cc: e.target.value
                            })
                          }
                          aria-label="CC email address"
                        />
                      </div>
                    </div>
                  </div>
                  <EmailTypeButtons
                    email={validEmail}
                    updateEmail={updateEmail}
                    userCanManageBooking={userCanManageBooking}
                    userIsInternal={userIsInternal}
                    userCanAccessBooking={userCanAccessBooking}
                    showPreOrders={validShowPreOrders}
                  />
                  <IncludeOptions
                    email={validEmail}
                    updateEmail={updateEmail}
                    userCanAccessBooking={userCanAccessBooking}
                    showCustomerRequests={showCustomerRequests}
                  />
                  <div className="SendEmailSection flex flex-col gap-2 flex-1">
                    <div className="SendEmailSection-Label font-medium text-[var(--textColor)] text-sm">
                      Attachments:
                    </div>
                    <div className="SendEmailSection-Data Attachments flex flex-row items-center flex-wrap gap-3 max-md:flex-col max-md:items-start">
                      <AttachmentsList
                        selectedAttachments={selectedAttachments}
                        message={validEmail}
                        updateEmail={updateEmail}
                      />
                      {preDefinedAttachments &&
                        preDefinedAttachments.length > 0 && (
                          <Select
                            value={selectedAttachment}
                            onValueChange={handleAttachmentSelect}
                          >
                            <SelectTrigger
                              style={{
                                marginBottom: "-2px",
                                alignSelf: "flex-end",
                                width: "max-content"
                              }}
                            >
                              <SelectValue placeholder="Select attachment" />
                            </SelectTrigger>
                            <SelectContent
                              container={portalContainerRef.current}
                            >
                              {preDefinedAttachments.map(
                                (attachment, index) => (
                                  <SelectItem
                                    key={`${attachment._id}-${index}`}
                                    value={attachment._id}
                                  >
                                    {attachment.name}
                                  </SelectItem>
                                )
                              )}
                            </SelectContent>
                          </Select>
                        )}
                      <input
                        type="file"
                        className="UploadPicker w-full max-w-[200px] rounded-[calc(var(--radius)-2px)] border border-solid border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm text-[hsl(var(--foreground))] cursor-pointer max-md:max-w-full [&::-webkit-file-upload-button]:hidden [&::file-selector-button]:hidden"
                        key={`${selectedAttachments?.[0]?.name}`}
                        multiple={false}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,image/*"
                        onChange={handleFileUpload}
                      />
                    </div>
                  </div>
                  <div className="SendEmailSection-Row flex flex-row flex-wrap gap-4 justify-between items-start max-md:flex-col max-md:gap-4">
                    <div className="SendEmailSection flex flex-col gap-2 flex-1">
                      <div className="SendEmailSection-Label font-medium text-[var(--textColor)] text-sm">
                        Booking details:
                      </div>
                      <span>{bookingDetails}</span>
                    </div>
                    {preDefinedMessages && preDefinedMessages.length > 0 && (
                      <div className="SendEmailSection SendEmailSection-Right flex flex-col gap-2 flex-1 items-end text-right max-md:items-start max-md:text-left">
                        <div className="SendEmailSection-Label font-medium text-[var(--textColor)] text-sm">
                          Predefined message:
                        </div>
                        <div className="SendEmailSection-Data flex flex-col gap-2">
                          <Select
                            value={preDefinedMessage}
                            onValueChange={(value) => {
                              setPreDefinedMessage(value);
                            }}
                          >
                            <SelectTrigger
                              style={{
                                marginBottom: "-2px",
                                alignSelf: "flex-end",
                                width: "max-content"
                              }}
                            >
                              <SelectValue placeholder="Select predefined message" />
                            </SelectTrigger>
                            <SelectContent
                              container={portalContainerRef.current}
                            >
                              {preDefinedMessages.map((message, index) => (
                                <SelectItem
                                  key={`${message.contents}-${index}`}
                                  value={message.contents}
                                >
                                  {message.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="SendEmail-Body pt-4 grow">
                <div className="SendEmail-BodyScroll h-full">
                  <HtmlEditor
                    body={validEmail.message ?? ""}
                    onEditorUpdate={(message) => {
                      updateEmail({ message });
                    }}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        <div className="SendEmail-Buttons sticky bottom-0 mt-auto pt-4 pb-4 shrink-0 flex gap-3 justify-between border-t border-solid border-[var(--borderColor)] bg-[var(--panelBackground)] z-10 max-md:flex-col-reverse max-md:gap-2">
          <Button
            className="ActionButton min-w-[100px] max-md:w-full"
            fill="outline"
            type="default"
            onClick={() => handleClose()}
          >
            {showEmailPreview ? "Discard" : "Cancel"}
          </Button>
          <div className="ButtonsRight flex gap-3 max-md:flex-col max-md:w-full">
            <Button
              className="ActionButton min-w-[100px] max-md:w-full"
              fill="outline"
              expand={true}
              type="default"
              onClick={async () => {
                if (showEmailPreview) {
                  setEmailPreview("");
                  return;
                }
                setLoadingPreview(true);
                const preview = await previewEmail(
                  validSelectedBooking.id ?? "",
                  validEmail
                );
                setEmailPreview(preview);
                setLoadingPreview(false);
              }}
            >
              {showEmailPreview ? "Edit" : "Preview"}
            </Button>
            <Button
              disabled={!userCanCreateMessages}
              className="ActionButton-Send min-w-[100px] max-md:w-full"
              fill="solid"
              expand={true}
              type="primary"
              onClick={async () => {
                setSending(true);
                await sendEmail(validSelectedBooking.id ?? "", validEmail);
                handleSent();
              }}
            >
              Send
            </Button>
          </div>
        </div>
      </div>
    </DetachedModal>
  );
};

export default React.memo<SendEmailType>(SendEmail);
